import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../lib/supabase';

const scrollToTarget = (targetId) => {
  const el = document.getElementById(targetId);
  const header = document.querySelector('header');
  if (!el) return;

  const headerOffset = header ? header.getBoundingClientRect().height + 12 : 88;
  const top = el.getBoundingClientRect().top + window.scrollY - headerOffset;

  window.scrollTo({
    top: Math.max(top, 0),
    behavior: 'smooth',
  });
};

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const hamburgerRef = useRef(null);
  const mobileMenuRef = useRef(null);

  useEffect(() => {
    // Get initial user
    supabase.auth.getUser().then(({ data: { user } }) => {
      setUser(user);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
      if (event === 'SIGNED_OUT') {
        navigate('/');
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  const closeMobileMenu = useCallback(() => {
    setIsOpen(false);
    hamburgerRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') closeMobileMenu();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, closeMobileMenu]);

  const handleLogout = async () => {
    // Always clear impersonation state on logout
    localStorage.removeItem('goodlink:impersonation_backup');
    localStorage.removeItem('goodlink:impersonation_active');
    try {
      await supabase.auth.signOut();
    } finally {
      // Defensive cleanup even if signOut throws
      localStorage.removeItem('goodlink:impersonation_backup');
      localStorage.removeItem('goodlink:impersonation_active');
      setIsOpen(false);
      // Refresh the page after logout
      window.location.reload();
    }
  };

  const navLinks = [
    { name: 'Pricing', href: '#pricing', id: 'pricing' },
    { name: 'Features', href: '#features', id: 'features' },
    { name: 'Insights', href: '#insights', id: 'insights' },
  ];

  const handleSectionClick = (e, sectionId) => {
    if (isOpen) setIsOpen(false);
    const isHome = location.pathname === '/';
    if (!isHome) {
      navigate(`/#${sectionId}`);
      return;
    }
    e.preventDefault();
    window.history.replaceState(null, '', `#${sectionId}`);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scrollToTarget(sectionId);
      });
    });
  };

  return (
    <header className="sticky top-0 z-50 flex flex-col border-b border-solid border-[#0b996f]/20 bg-[#d7fec8] backdrop-blur-md">
      <div className="flex items-center justify-between px-6 py-4 lg:px-20">
        <Link
          to="/"
          onClick={(e) => {
            if (location.pathname === '/') {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }
          }}
          className="flex items-center gap-3 text-[#1b1b1b] transition-colors hover:text-[#0b996f] cursor-pointer"
        >
          {/* Link-shape icon hidden by request (kept in code, do not delete)
          <div className="size-5 sm:size-8 text-primary flex-shrink-0" aria-hidden="true">
            <svg fill="none" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"
                stroke="#a855f7"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="3"
              ></path>
              <path
                d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"
                stroke="#a855f7"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="3"
              ></path>
            </svg>
          </div>
          */}
          <span className="text-3xl font-black leading-tight tracking-tight text-inherit">
            GoodLink
          </span>
        </Link>

        {/* Desktop Navigation */}
        <nav aria-label="Main" className="hidden md:flex flex-1 justify-center">
          <div className="flex items-center gap-9">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                onClick={(e) => {
                  e.preventDefault();
                  handleSectionClick(e, link.id);
                }}
                className="text-[#1b1b1b] hover:text-primary transition-colors text-base font-bold leading-normal cursor-pointer"
              >
                {link.name}
              </a>
            ))}
          </div>
        </nav>

        <div className="flex items-center gap-2">
          {/* Action Buttons - Hidden on mobile, shown in menu */}
          <div className="hidden sm:flex items-center gap-2">
            {user ? (
              <>
                <Link
                  to="/dashboard/links"
                  className="flex min-w-[84px] cursor-pointer items-center justify-center overflow-hidden rounded-lg h-10 px-4 bg-[#a855f7] hover:bg-[#9333ea] text-white text-sm font-bold leading-normal tracking-[0.015em] transition-colors"
                >
                  <span className="truncate">Dashboard</span>
                </Link>
                <button
                  onClick={handleLogout}
                  className="flex min-w-[84px] cursor-pointer items-center justify-center overflow-hidden rounded-lg h-10 px-4 bg-[#c0ffa5] hover:bg-[#b0ef95] text-[#1b1b1b] text-sm font-bold leading-normal tracking-[0.015em] transition-colors"
                >
                  <span className="truncate">Logout</span>
                </button>
              </>
            ) : (
              <Link
                to="/login"
                className="flex min-w-[84px] cursor-pointer items-center justify-center overflow-hidden rounded-lg h-10 px-4 bg-[#a855f7] hover:bg-[#9333ea] text-white text-sm font-bold leading-normal tracking-[0.015em] transition-colors"
              >
                <span className="truncate">Login</span>
              </Link>
            )}
          </div>

          <button
            ref={hamburgerRef}
            onClick={() => setIsOpen(!isOpen)}
            aria-expanded={isOpen}
            aria-controls="mobile-menu"
            aria-label={isOpen ? 'Close menu' : 'Open menu'}
            className="flex md:hidden items-center justify-center size-10 rounded-lg text-[#1b1b1b] hover:bg-black/5 transition-colors"
          >
            <span className="material-symbols-outlined text-2xl" aria-hidden="true">{isOpen ? 'close' : 'menu'}</span>
          </button>
        </div>
      </div>

      <AnimatePresence mode="wait">
        {isOpen && (
          <motion.div
            id="mobile-menu"
            ref={mobileMenuRef}
            role="region"
            aria-label="Mobile navigation"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
            className="md:hidden flex flex-col border-t border-primary/20 bg-white overflow-hidden"
          >
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2, delay: 0.05 }}
              className="p-6 flex flex-col gap-6"
            >
              <nav aria-label="Mobile" className="flex flex-col gap-4">
                {navLinks.map((link) => (
                  <a
                    key={link.name}
                    href={link.href}
                    onClick={(e) => {
                      e.preventDefault();
                      handleSectionClick(e, link.id);
                    }}
                    className="text-[#1b1b1b] hover:text-primary transition-colors text-xl font-bold cursor-pointer"
                  >
                    {link.name}
                  </a>
                ))}
                {user && (
                  <Link
                    to="/dashboard/links"
                    onClick={closeMobileMenu}
                    className="text-[#1b1b1b] hover:text-primary transition-colors text-lg font-bold"
                  >
                    Dashboard
                  </Link>
                )}
              </nav>
              <div className="flex flex-col gap-3 pt-6 border-t border-primary/20">
                {user ? (
                  <button
                    onClick={() => {
                      handleLogout();
                      closeMobileMenu();
                    }}
                    className="flex w-full cursor-pointer items-center justify-center rounded-lg h-12 bg-[#c0ffa5] hover:bg-[#b0ef95] text-[#1b1b1b] font-bold transition-colors"
                  >
                    Logout
                  </button>
                ) : (
                  <Link
                    to="/login"
                    onClick={closeMobileMenu}
                    className="flex w-full cursor-pointer items-center justify-center rounded-lg h-12 bg-[#a855f7] hover:bg-[#9333ea] text-white font-bold transition-colors"
                  >
                    Login
                  </Link>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
